import { Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getStatusesTask } from '../../constants/options';
import { INPUT_FILE_ACCEPT_DOC } from '../../constants';
import { formatDateTask } from '../../utils/taskUtils';
import { commonTextAreaStyles, commonTextFieldStyles } from '../../utils/textFieldStyles';
import DragDropFileUpload from '../DragDropFileUpload';
import CustomFieldsForm from '../CustomFieldsForm';
import styles from './styles.module.css';

/**
 * TaskForm component - Form for updating task status and adding comments/files
 */
export default function TaskForm({
  form,
  errorResponse,
  selectedFile,
  taskData,
  handleChange,
  handleFileChange,
  submitTrigger,
}) {
  const { t } = useTranslation();
  const statusesTask = getStatusesTask(t);

  if (!taskData) return null;

  const today = new Date().toISOString().split('T')[0];
  const isOverdue = formatDateTask(taskData?.updatedAt) < today;

  return (
    <div className={styles.fields}>
      {isOverdue && (
        <Typography
          color="error"
          variant="body2"
          sx={{ fontWeight: 'bold', mb: 1 }}
        >
          ⚠️ {t('modals.overdueTaskWarning')}
        </Typography>
      )}

      <TextField
        label={t('modals.comments')}
        placeholder={
          isOverdue
            ? t('modals.expalainDelay')
            : t('modals.provideSpecific')
        }
        variant="outlined"
        value={form.comments}
        name="comments"
        onChange={handleChange}
        multiline
        rows={5}
        fullWidth
        required={isOverdue}
        error={isOverdue && (!form.comments || form.comments.trim() === '')}
        helperText={isOverdue && (!form.comments || form.comments.trim() === '') ? t('modals.commentRequired') : ''}
        sx={{
          ...commonTextAreaStyles,
          ...(isOverdue && {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'error.main',
              },
            },
          }),
        }}
      />

      <TextField
        select
        value={form.status}
        variant="outlined"
        label={t('generic.status')}
        name="status"
        fullWidth
        onChange={handleChange}
        sx={commonTextFieldStyles}
      >
        {statusesTask.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <FormControlLabel
        control={
          <Checkbox
            checked={form.performedByExternalProvider || false}
            onChange={handleChange}
            name="performedByExternalProvider"
          />
        }
        label={t('modals.performedByExternalProvider')}
      />

      <DragDropFileUpload
        accept={INPUT_FILE_ACCEPT_DOC}
        selectedFile={selectedFile}
        onFileChange={handleFileChange}
        onFileRemove={() => handleFileChange(null)}
        helperText={t('generic.uploadFileMessage')}
        placeholder={t('modals.uploadFile')}
      />

      <CustomFieldsForm
        maintenanceId={taskData?.maintenanceId}
        machineMaintenanceId={taskData?.id}
        submitTrigger={submitTrigger}
      />

      {errorResponse &&
        (Array.isArray(errorResponse) ? (
          errorResponse.map((item, index) => (
            <Typography key={index} color="error">
              {item}
            </Typography>
          ))
        ) : (
          <Typography color="error">{errorResponse}</Typography>
        ))}
    </div>
  );
}
